import ReactDOM from "react-dom";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, MapPin, Clock, AlertCircle, CheckCircle, Activity, FileText, Camera, User } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  pending: "bg-slate-100 text-slate-600", ci_done: "bg-blue-100 text-blue-700",
  dtm_done: "bg-purple-100 text-purple-700", dtc_done: "bg-yellow-100 text-yellow-700",
  mi_done: "bg-orange-100 text-orange-700", qc_passed: "bg-green-100 text-green-700",
  rework: "bg-red-100 text-red-700", paid: "bg-emerald-100 text-emerald-700"
};

export default function DTDetailPage() {
  const { dtId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dt, setDT] = useState(null);
  const [events, setEvents] = useState([]);
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [holdReasons, setHoldReasons] = useState([]);
  const [showHold, setShowHold] = useState(false);
  const [holdData, setHoldData] = useState({ reason_code: "", responsibility: "inovit", notes: "" });

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [dtRes, eventsRes, reasonsRes, surveyRes] = await Promise.all([
          api.get(`/dt-master/${dtId}`),
          api.get(`/dt-master/${dtId}/history`),
          api.get("/hold-reasons"),
          api.get(`/installation/survey/${dtId}`).catch(() => ({ data: null }))
        ]);
        setDT(dtRes.data);
        setEvents(eventsRes.data);
        setHoldReasons(reasonsRes.data);
        setSurvey(surveyRes.data);
      } catch (e) {
        toast.error("Failed to load DT");
        navigate("/dt-master");
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [dtId]);

  const handleHoldToggle = async () => {
    if (dt.hold_status !== "none") {
      try {
        await api.patch(`/dt-master/${dtId}/hold`, { action: "release" });
        toast.success("Hold released");
        setDT({ ...dt, hold_status: "none" });
      } catch (e) {
        toast.error(e.response?.data?.detail || "Error");
      }
    } else {
      setShowHold(true);
    }
  };

  const handlePlaceHold = async () => {
    try {
      await api.patch(`/dt-master/${dtId}/hold`, {
        action: "place", reason_code: holdData.reason_code,
        responsibility: holdData.responsibility, notes: holdData.notes
      });
      toast.success("Hold placed");
      setDT({ ...dt, hold_status: "on_hold", current_hold_reason_code: holdData.reason_code });
      setShowHold(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const getNextAction = () => {
    const map = {
      pending: { label: "Start Survey", path: `/survey/${dtId}`, color: "bg-blue-500" },
      ci_done: { label: "Do DTM", path: `/dtm/${dtId}`, color: "bg-purple-500" },
      dtm_done: { label: "Do DTC", path: `/dtc/${dtId}`, color: "bg-yellow-500" },
      dtc_done: { label: "Do MI", path: `/mi/${dtId}`, color: "bg-orange-500" },
    };
    return map[dt?.lifecycle_status];
  };

  if (loading) return <div className="p-6 text-slate-400">Loading...</div>;
  if (!dt) return null;

  const nextAction = getNextAction();

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto animate-fade-up" data-testid="dt-detail-page">
      {/* Back */}
      <button onClick={() => navigate("/dt-master")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4">
        <ArrowLeft size={14} />Back to DT Master
      </button>

      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-sm p-5 mb-4 shadow-sm">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono text-lg font-black text-slate-900">{dt.dt_code}</div>
            <div className="text-slate-600 text-sm mt-0.5">{dt.dt_name || "No name"}</div>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${STATUS_COLORS[dt.lifecycle_status] || "bg-slate-100 text-slate-600"}`}>
                {dt.lifecycle_status?.replace("_", " ")}
              </span>
              {dt.hold_status !== "none" && (
                <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold bg-red-100 text-red-700 uppercase">
                  {dt.hold_status}
                </span>
              )}
              {dt.is_locked && (
                <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold bg-orange-100 text-orange-700 uppercase">
                  Financially Locked
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {nextAction && dt.hold_status === "none" && (
              <button
                onClick={() => navigate(nextAction.path)}
                className={`px-4 py-2 ${nextAction.color} text-white rounded-sm text-sm font-bold hover:opacity-90`}
                data-testid="dt-next-action-btn"
              >
                {nextAction.label}
              </button>
            )}
            {["admin", "ci", "supervisor"].includes(user?.role) && (
              <button
                onClick={handleHoldToggle}
                className={`px-4 py-2 rounded-sm text-sm font-bold ${dt.hold_status !== "none" ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-red-100 text-red-700 hover:bg-red-200"}`}
                data-testid="hold-toggle-btn"
              >
                {dt.hold_status !== "none" ? "Release Hold" : "Place Hold"}
              </button>
            )}
          </div>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-slate-100">
          <div>
            <div className="text-[10px] text-slate-400 uppercase tracking-wide">Feeder</div>
            <div className="font-mono text-xs font-semibold text-slate-700">{dt.feeder_code}</div>
          </div>
          <div>
            <div className="text-[10px] text-slate-400 uppercase tracking-wide">Circle</div>
            <div className="text-sm font-semibold text-slate-700">{dt.circle_name}</div>
          </div>
          <div>
            <div className="text-[10px] text-slate-400 uppercase tracking-wide">Capacity</div>
            <div className="text-sm font-semibold text-slate-700">{dt.transformer_capacity_kva ? `${dt.transformer_capacity_kva} KVA` : "—"}</div>
          </div>
          <div>
            <div className="text-[10px] text-slate-400 uppercase tracking-wide">GPS</div>
            <div className="text-xs font-mono text-slate-600">{dt.gps_lat ? `${dt.gps_lat.toFixed(4)}, ${dt.gps_lng.toFixed(4)}` : "—"}</div>
          </div>
          <div className="col-span-2 md:col-span-4">
            <div className="text-[10px] text-slate-400 uppercase tracking-wide">Address</div>
            <div className="text-sm text-slate-700">{dt.address || "—"}</div>
          </div>
        </div>
      </div>

      {/* Survey Data */}
      {survey && (
        <div className="bg-white border border-slate-200 rounded-sm p-4 mb-4 shadow-sm" data-testid="survey-data-section">
          <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-3 flex items-center gap-2">
            <FileText size={14} className="text-blue-500" />Survey Data
            <span className="text-[10px] font-normal text-slate-400 ml-auto">
              v{survey.version} {survey.is_resurvey ? "(Resurvey)" : ""} &bull; {survey.survey_datetime ? new Date(survey.survey_datetime).toLocaleString("en-IN") : "—"}
            </span>
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <div className="text-[10px] text-slate-400 uppercase">Capacity</div>
              <div className="text-sm font-semibold text-slate-700">{survey.transformer_capacity_kva ? `${survey.transformer_capacity_kva} KVA` : "—"}</div>
            </div>
            <div>
              <div className="text-[10px] text-slate-400 uppercase">Cable Type</div>
              <div className="text-sm font-semibold text-slate-700">{survey.cable_type || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] text-slate-400 uppercase">Cable Size</div>
              <div className="text-sm font-semibold text-slate-700">{survey.cable_size_sqmm ? `${survey.cable_size_sqmm} sq mm` : "—"}</div>
            </div>
            <div>
              <div className="text-[10px] text-slate-400 uppercase">Cable Length</div>
              <div className="text-sm font-semibold text-slate-700">{survey.cable_length_m ? `${survey.cable_length_m} m` : "—"}</div>
            </div>
            <div>
              <div className="text-[10px] text-slate-400 uppercase">GPS</div>
              <div className="text-xs font-mono text-slate-600">{survey.gps_lat ? `${survey.gps_lat.toFixed(4)}, ${survey.gps_lng.toFixed(4)}` : "—"}</div>
            </div>
            <div>
              <div className="text-[10px] text-slate-400 uppercase">GPS Accuracy</div>
              <div className="text-sm font-semibold text-slate-700">{survey.gps_accuracy_m != null ? `${survey.gps_accuracy_m} m` : "—"}</div>
            </div>
            <div>
              <div className="text-[10px] text-slate-400 uppercase">Old Meter</div>
              <div className="text-sm font-semibold text-slate-700">{survey.old_meter_available ? `Yes (${survey.old_meter_company || "—"})` : "No"}</div>
            </div>
            <div>
              <div className="text-[10px] text-slate-400 uppercase">Status</div>
              <div className="text-sm font-semibold text-slate-700 capitalize">{survey.status || "—"}</div>
            </div>
          </div>
          {survey.remarks && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <div className="text-[10px] text-slate-400 uppercase">Remarks</div>
              <div className="text-sm text-slate-700">{survey.remarks}</div>
            </div>
          )}
          {survey.photos?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <div className="text-[10px] text-slate-400 uppercase mb-2 flex items-center gap-1"><Camera size={10} /> Photos ({survey.photos.length})</div>
              <div className="flex gap-2 flex-wrap">
                {survey.photos.map((p, i) => (
                  <a key={i} href={p} target="_blank" rel="noreferrer" className="w-16 h-16 bg-slate-100 rounded border border-slate-200 flex items-center justify-center text-[10px] text-slate-400 hover:border-blue-300">
                    <Camera size={16} />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Event History */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 shadow-sm">
        <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-4 flex items-center gap-2">
          <Activity size={14} className="text-orange-500" />Event History
        </h3>
        {events.length === 0 ? (
          <div className="text-sm text-slate-400 py-4 text-center">No events yet</div>
        ) : (
          <div className="space-y-3">
            {events.map(event => (
              <div key={event.id} className="flex gap-3 items-start">
                <div className="w-2 h-2 rounded-full bg-orange-400 mt-1.5 flex-shrink-0"></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">{event.event_type?.replace("_", " ")}</span>
                    <span className="text-[10px] text-slate-400">
                      {event.from_status} → {event.to_status}
                    </span>
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    {event.performed_at ? new Date(event.performed_at).toLocaleString("en-IN") : "—"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Hold Modal */}
      {showHold && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-sm p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-bold mb-4" style={{ fontFamily: "Manrope" }}>Place Hold on DT</h3>
            <div className="space-y-4">
              <div>
                <label className="field-label">Hold Reason *</label>
                <select className="field-select" value={holdData.reason_code} onChange={e => setHoldData({ ...holdData, reason_code: e.target.value })} data-testid="hold-reason-select">
                  <option value="">Select reason...</option>
                  {holdReasons.map(r => <option key={r.code} value={r.code}>{r.reason_name}</option>)}
                </select>
              </div>
              <div>
                <label className="field-label">Responsibility</label>
                <select className="field-select" value={holdData.responsibility} onChange={e => setHoldData({ ...holdData, responsibility: e.target.value })}>
                  <option value="inovit">INOVIT</option>
                  <option value="gmr">GMR</option>
                  <option value="discom">DISCOM</option>
                  <option value="site_condition">Site Condition</option>
                  <option value="external">External</option>
                </select>
              </div>
              <div>
                <label className="field-label">Notes</label>
                <textarea className="field-input h-20 resize-none" value={holdData.notes} onChange={e => setHoldData({ ...holdData, notes: e.target.value })} />
              </div>
              <div className="flex gap-3">
                <button onClick={() => setShowHold(false)} className="flex-1 px-4 py-2 border-2 border-slate-200 rounded-sm text-sm font-semibold text-slate-600">Cancel</button>
                <button onClick={handlePlaceHold} disabled={!holdData.reason_code} className="flex-1 px-4 py-2 bg-red-500 text-white rounded-sm text-sm font-bold hover:bg-red-600 disabled:opacity-50">
                  Place Hold
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
